import { Box } from "@mui/material";
import { Fragment, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { SITE_MEDIA } from "src/domains/shared/constants/styles/site";
import {
  isTitlePhraseLanguage,
  splitTitlePhrases,
  type TitlePhraseLanguage,
} from "./titlePhrases";

/**
 * 사이트 문구 안 꾸밈 태그 — 번역마다 줄바꿈 자리가 달라 문구 안에 태그로 둔다.
 * `<br/>` 언제나 줄바꿈 · `<brd/>` 넓은 화면만(시안 `.br-d`, 640px 이하 숨김) · `<brm/>` 좁은 화면만(시안 `.br-m`)
 * · `<b>…</b>` 굵게 · `<em>…</em>` 강조. 문구는 우리 번역 리소스뿐이라 이 다섯 태그만 읽고 나머지는 글자 그대로 둔다.
 */
const TAG_PATTERN = /(<br\/>|<brd\/>|<brm\/>|<b>.*?<\/b>|<em>.*?<\/em>)/;

const BREAK_STYLES = {
  "<brd/>": { [SITE_MEDIA.phone]: { display: "none" } },
  "<brm/>": { display: "none", [SITE_MEDIA.phone]: { display: "inline" } },
} as const;

/** 구마다 줄을 바꾸지 않는 상자(`[data-phrase]`, 사이트 전역 규칙)로 감싼다 — 공백은 그대로 줄바꿈 자리 */
const renderPhrases = (text: string, language: TitlePhraseLanguage) =>
  splitTitlePhrases(text, language).map((token, index) =>
    /^[\s\u200B]+$/u.test(token) ? (
      token
    ) : (
      <span data-phrase="" key={index}>
        {token}
      </span>
    )
  );

export function renderSiteText(
  text: string,
  phraseLanguage?: TitlePhraseLanguage
): ReactNode[] {
  const plain = (value: string) =>
    phraseLanguage ? renderPhrases(value, phraseLanguage) : value;
  return text.split(TAG_PATTERN).map((part, index) => {
    if (part === "<br/>") return <br key={index} />;
    if (part === "<brd/>" || part === "<brm/>")
      return <Box component="br" key={index} sx={BREAK_STYLES[part]} />;
    if (part.startsWith("<b>"))
      return <b key={index}>{plain(part.slice(3, -4))}</b>;
    if (part.startsWith("<em>"))
      return <em key={index}>{plain(part.slice(4, -5))}</em>;
    return phraseLanguage ? (
      <Fragment key={index}>{plain(part)}</Fragment>
    ) : (
      part
    );
  });
}

/** 꾸밈 태그가 든 번역 문구를 그린다 */
export function SiteText({ text }: { text: string }) {
  return <>{renderSiteText(text)}</>;
}

/**
 * 제목 문구 — `SiteText` 에 더해, 낱말 사이를 띄우지 않는 일본어·크메르어는 구 단위로만 줄을 바꾼다.
 * 구 하나가 한 줄보다 길면 그 구 안에서 줄을 바꾼다(넘치지 않는다).
 */
export function SiteTitle({ text }: { text: string }) {
  const { i18n } = useTranslation();
  const language = i18n.language?.split("-")[0];
  return (
    <>
      {renderSiteText(
        text,
        isTitlePhraseLanguage(language) ? language : undefined
      )}
    </>
  );
}
